<template>
  <div class="options734-tab-content">
    <!-- Program 734 Options Toggle -->
    <div class="form-section border rounded p-1 mb-1">
      <b-row>
        <b-col cols="12">
          <b-form-group class="mb-0">
            <b-form-checkbox
              :checked="localDeviceForm.program734Options === 'Y'"
              @change="updateProgram734Options"
              switch
              size="md"
              :disabled="!isDoor"
            >
              <strong>{{ $t("dmp.deviceSetup.program734Options") }}</strong>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- 734 Options Content -->
    <div v-if="localDeviceForm.program734Options === 'Y'">
      <!-- Zone 2 Options Section -->
      <div class="form-section border rounded p-2 mb-1">
        <h6 class="mb-2 font-weight-bold">Zone 2</h6>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group>
              <b-form-checkbox
                :checked="extConfig.activateZone2Bypass === 'Y'"
                @change="updateExtConfig('activateZone2Bypass', $event)"
              >
                {{ $t("dmp.deviceSetup.activateZone2Bypass") }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group>
              <b-form-checkbox
                :checked="extConfig.relockOnZone2Change === 'Y'"
                @change="updateExtConfig('relockOnZone2Change', $event)"
              >
                Relock onZone 2 Change
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group
              :label="$t('dmp.deviceSetup.zone2BypassTime')"
              label-for="zone2BypassTime"
            >
              <b-input-group>
                <b-form-input
                  id="zone2BypassTime"
                  :value="parseInt(extConfig.zone2BypassTime) || 0"
                  @input="updateExtConfigPadded('zone2BypassTime', $event, 3)"
                  type="number"
                  min="0"
                  max="999"
                  :disabled="isZone2BypassTimeDisabled"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Zone 3 Options Section -->
      <div class="form-section border rounded p-2 mb-1">
        <h6 class="mb-2 font-weight-bold">Zone 3</h6>
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center">
            <b-form-group>
              <b-form-checkbox
                :checked="extConfig.activateZone3RequestExit === 'Y'"
                @change="updateExtConfig('activateZone3RequestExit', $event)"
              >
                {{ $t("dmp.deviceSetup.activateZone3ReqToExit") }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group
              :label="$t('dmp.deviceSetup.zone3ReqToExitStrikeTime')"
              label-for="requestExitStrikeTime"
            >
              <b-input-group>
                <b-form-input
                  id="requestExitStrikeTime"
                  :value="parseInt(extConfig.requestExitStrikeTime) || 0"
                  @input="updateExtConfigPadded('requestExitStrikeTime', $event, 3)"
                  type="number"
                  min="0"
                  max="99"
                  :disabled="isRequestExitStrikeTimeDisabled"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Communication & Speaker Section -->
      <div class="form-section border rounded p-2 mb-1">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center">
            <b-form-group>
              <b-form-checkbox
                :checked="extConfig.activateOnboardSpeaker === 'Y'"
                @change="updateExtConfig('activateOnboardSpeaker', $event)"
              >
                {{ $t("dmp.deviceSetup.activateOnboardSpeaker") }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group
              :label="$t('dmp.deviceSetup.noCommunicationWithPanel')"
              label-for="noCommWithPanel"
            >
              <b-form-select
                id="noCommWithPanel"
                :value="extConfig.noCommWithPanel"
                @change="updateExtConfigDirect('noCommWithPanel', $event)"
                :options="communicationPanelOptions"
                :disabled="isNoCommWithPanelDisabled"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Card Options Section -->
      <div class="form-section border rounded p-2 mb-1">
        <h6 class="mb-2 font-weight-bold">{{ $t("dmp.deviceSetup.cardOptions") }}</h6>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              :label="$t('dmp.deviceSetup.cardFormat')"
              label-for="cardOption"
            >
              <b-form-select
                id="cardOption"
                :value="extConfig.cardOption"
                @change="updateExtConfigDirect('cardOption', $event)"
                :options="cardFormatOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Site Code Options Section -->
      <div class="form-section border rounded p-2 mb-1">
        <h6 class="mb-2 font-weight-bold">{{ $t("dmp.deviceSetup.siteCodes") }}</h6>
        <b-row>
          <b-col cols="12" class="d-flex align-items-center">
            <b-form-group>
              <b-form-checkbox
                :checked="extConfig.requireSiteCode === 'Y'"
                @change="updateExtConfig('requireSiteCode', $event)"
                :disabled="!isCardFormatFieldsDisabled"
              >
                {{ $t("dmp.deviceSetup.requireSiteCode") }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>

        </b-row>
      </div>
      <div class="form-section border rounded p-2">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              :label="$t('dmp.deviceSetup.numberOfUserCodeDigits')"
              label-for="numUserCodeDigits"
            >
              <b-input-group>
                <b-form-input
                  :disabled="!isCardFormatFieldsDisabled"
                  id="numUserCodeDigits"
                  :value="parseInt(extConfig.numUserCodeDigits) || 0"
                  @input="updateExtConfigPadded('numUserCodeDigits', $event, 2)"
                  type="number"
                  min="0"
                  max="9"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Disabled State Message -->
    <b-alert v-else variant="info" show class="mt-3">
      {{ $t("dmp.deviceSetup.enable734ToConfigureOptions") }}
    </b-alert>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupText,
  BAlert,
} from "bootstrap-vue";
import { ref, computed, watch } from "@vue/composition-api";

export default {
  name: "Options734Tab",
  components: {
    BRow,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupText,
    BAlert,
  },
  props: {
    deviceForm: {
      type: Object,
      required: true,
    },
    selectedDevice: {
      type: Object,
      default: () => null,
    },
    isDoor: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const localDeviceForm = ref({ ...props.deviceForm });

    const communicationPanelOptions = computed(() => [
      { value: "0", text: "Off" },
      { value: "1", text: "Site" },
      { value: "2", text: "Any" },
      { value: "3", text: "On" },
      { value: "4", text: "Last" },
    ]);

    const cardFormatOptions = computed(() => [
      { value: "0", text: "DMP" },
      { value: "1", text: "Custom" },
      { value: "2", text: "Any" },
    ]);

    const cardSettings = computed(() => [
      {
        key: "siteCodeLength",
        label: "dmp.deviceSetup.siteCodeLength",
        padLength: 2,
        max: 99,
      },
      {
        key: "siteCodePosition",
        label: "dmp.deviceSetup.siteCodePosition",
        padLength: 3,
        max: 999,
      },
      {
        key: "userCodeLength",
        label: "dmp.deviceSetup.userCodeLength",
        padLength: 2,
        max: 54,
      },
      {
        key: "userCodePosition",
        label: "dmp.deviceSetup.userCodePosition",
        padLength: 3,
        max: 999,
      },
    ]);

    const extConfig = computed(() => localDeviceForm.value.extendedConfig || {});

    const isZone2BypassTimeDisabled = computed(() => {
      return extConfig.value.activateZone2Bypass !== "Y";
    });

    const isRequestExitStrikeTimeDisabled = computed(() => {
      return extConfig.value.activateZone3RequestExit !== "Y";
    });

    const isNoCommWithPanelDisabled = computed(() => {
      return extConfig.value.activateOnboardSpeaker !== "Y";
    });

    const isCardFormatFieldsDisabled = computed(() => {
      return extConfig.value.cardOption === "0"; // DMP
    });

    const areSiteCodesDisabled = computed(() => {
      return extConfig.value.requireSiteCode !== "Y";
    });

    const updateProgram734Options = (value) => {
      const newValue = value ? "Y" : "N";
      const updated = {
        ...localDeviceForm.value,
        program734Options: newValue,
        extendedConfig: { ...(localDeviceForm.value.extendedConfig || {}) },
      };
      localDeviceForm.value = updated;
      emit("update:device-form", updated);
    };

    const updateExtConfig = (key, value) => {
      const updated = {
        ...localDeviceForm.value,
        extendedConfig: {
          ...(localDeviceForm.value.extendedConfig || {}),
          [key]: value ? "Y" : "N",
        },
      };
      localDeviceForm.value = updated;
      emit("update:device-form", updated);
    };

    const updateExtConfigDirect = (key, value) => {
      const updated = {
        ...localDeviceForm.value,
        extendedConfig: {
          ...(localDeviceForm.value.extendedConfig || {}),
          [key]: value,
        },
      };
      localDeviceForm.value = updated;
      emit("update:device-form", updated);
    };

    const updateExtConfigPadded = (key, value, length) => {
      const padded = String(value).padStart(length, "0");
      const updated = {
        ...localDeviceForm.value,
        extendedConfig: {
          ...(localDeviceForm.value.extendedConfig || {}),
          [key]: padded,
        },
      };
      localDeviceForm.value = updated;
      emit("update:device-form", updated);
    };

    const updateExtConfigString = (key, value) => {
      const padded = (value || "").padEnd(3, " ");
      const updated = {
        ...localDeviceForm.value,
        extendedConfig: {
          ...(localDeviceForm.value.extendedConfig || {}),
          [key]: padded,
        },
      };
      localDeviceForm.value = updated;
      emit("update:device-form", updated);
    };

    watch(
      () => props.deviceForm,
      (newValue) => {
        localDeviceForm.value = {
          ...newValue,
          extendedConfig: { ...(newValue?.extendedConfig || {}) },
        };
      },
      { deep: true }
    );

    return {
      localDeviceForm,
      extConfig,
      updateProgram734Options,
      updateExtConfig,
      updateExtConfigDirect,
      updateExtConfigPadded,
      updateExtConfigString,
      communicationPanelOptions,
      cardFormatOptions,
      cardSettings,
      isZone2BypassTimeDisabled,
      isRequestExitStrikeTimeDisabled,
      isNoCommWithPanelDisabled,
      isCardFormatFieldsDisabled,
      areSiteCodesDisabled,
    };
  },
};
</script>

<style scoped lang="scss">
.form-section {
  border: 1px solid #ebe9f1;

  &:last-child {
    margin-bottom: 0;
  }

  h6 {
    color: #5e5873;
  }
}

.font-weight-bold {
  font-weight: 600;
}

// Dark mode adjustments
.dark-layout {
  .form-section {
    border-color: #404656;

    h6 {
      color: #b4b7bd;
    }
  }
}
</style>
